import { useState, useEffect, useRef } from 'react';
import { gettyApi } from '../utils/gettyApi';
import { logger } from '../utils/logger';

interface VocabularyState {
  signatures: string[]; // Not used, kept for compatibility
  workTypes: string[];
  techniques: string[];
  materials: string[];
  substrates: string[];
  loading: boolean;
  error: string | null;
}

export const useGettyVocabulary = () => {
  const [state, setState] = useState<VocabularyState>({
    signatures: [],
    workTypes: [],
    techniques: [],
    materials: [],
    substrates: [],
    loading: true,
    error: null,
  });

  // Prevent duplicate loads within the same component instance
  const loadStartedRef = useRef(false);

  useEffect(() => {
    // Guard against multiple effect runs (e.g., React StrictMode double-mounting)
    if (loadStartedRef.current) {
      return;
    }
    loadStartedRef.current = true;

    const loadVocabularies = async () => {
      try {
        // Fetch hierarchies - getMaterialsHierarchy is now cached, so calling it
        // twice (via getMaterials and getSubstrates) will only make one API request.
        // But for clarity and efficiency, we fetch the hierarchy once and extract
        // flatLabels for both materials and substrates.
        const [workTypesHierarchy, techniquesHierarchy, materialsHierarchy] = await Promise.all([
          gettyApi.getWorkTypesHierarchy(),
          gettyApi.getTechniquesHierarchy(),
          gettyApi.getMaterialsHierarchy(), // Single call for both materials & substrates
        ]);

        // Extract flatLabels from hierarchies
        const workTypes = workTypesHierarchy?.flatLabels ?? [];
        const techniques = techniquesHierarchy?.flatLabels ?? [];
        const materialsLabels = materialsHierarchy?.flatLabels ?? [];

        setState({
          signatures: [], // Signatures handled by useSignatureData
          workTypes,
          techniques,
          materials: materialsLabels,
          substrates: materialsLabels, // Same data source as materials
          loading: false,
          error: null,
        });
      } catch (error) {
        logger.error('Failed to load Getty vocabularies', error);
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load vocabulary data',
        }));
      }
    };

    loadVocabularies();
  }, []);

  return state;
};
